import MarketList from '@/components/modules/MarketList';
import OrderBook from '@/components/modules/OrderBook';
import AccountValue from '@/components/trader-modules/AccountValue';
import { useAppSelector } from '@/state/hooks';
import {
    selectXplorTraderDesktopLayoutNode,
    selectXplorTraderLaptopLayoutNode,
    selectXplorTraderMobileLayoutNode,
    selectXplorTraderTabletLayoutNode
} from '@/state/reducers/xplorTraderLayoutSlice';
import { useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import {
    MdAccountBalanceWallet,
    MdCandlestickChart,
    MdHistory,
    MdPivotTableChart,
    MdPriceChange,
    MdSwapVerticalCircle
} from 'react-icons/md';
import { MosaicNode } from 'react-mosaic-component';
import useLanguage from './useLanguage';
import { UseScreenSizesReturn } from './useScreenSizes';
import { ConfigType } from './useXplorSpotLayout';

export type XplorTraderModules = 'Tickers' | 'Account Value' | 'Blotter' | 'Markets' | 'Order Book' | 'Chart' | '';
export const xplorTraderTabList = ['Tickers', 'Account Value', 'Blotter', 'Markets', 'Order Book', 'Chart'];
export const xplorTraderTabIcon = {
    Tickers: MdSwapVerticalCircle,
    Blotter: MdHistory,
    'Account Value': MdAccountBalanceWallet,
    Chart: MdCandlestickChart,
    Markets: MdPivotTableChart,
    'Order Book': MdPriceChange
} as Record<XplorTraderModules, IconType>;

export const DEFAULT_LAYOUT: MosaicNode<XplorTraderModules> = {
    direction: 'row',
    first: {
        direction: 'column',
        first: {
            direction: 'row',
            first: 'Chart',
            second: 'Tickers',
            splitPercentage: 45
        },
        second: {
            direction: 'row',
            first: 'Order Book',
            second: 'Blotter',
            splitPercentage: 30
        },
        splitPercentage: 50
    },
    second: {
        direction: 'column',
        first: 'Markets',
        second: 'Account Value',
        splitPercentage: 68
    },
    splitPercentage: 78
};

export const DEFAULT_MOBILE_LAYOUT: MosaicNode<XplorTraderModules> = {
    direction: 'row',
    first: '',
    second: 'Tickers',
    splitPercentage: 0
};

export const DEFAULT_TABLET_LAYOUT: MosaicNode<XplorTraderModules> = {
    direction: 'row',
    first: 'Tickers',
    second: 'Chart',
    splitPercentage: 45
};

export const DEFAULT_LAPTOP_LAYOUT: MosaicNode<XplorTraderModules> = {
    direction: 'row',
    first: {
        direction: 'column',
        first: {
            direction: 'row',
            first: 'Chart',
            second: 'Tickers',
            splitPercentage: 50
        },
        second: {
            direction: 'row',
            first: 'Order Book',
            second: 'Blotter',
            splitPercentage: 30
        },
        splitPercentage: 50
    },
    second: {
        direction: 'column',
        first: 'Markets',
        second: 'Account Value',
        splitPercentage: 68
    },
    splitPercentage: 71
};

export interface UseXplorTraderLayoutReturn {
    layout: MosaicNode<XplorTraderModules> | null | undefined | any;
    default_layout: MosaicNode<XplorTraderModules>;
    config: ConfigType;
}

const useXplorTraderLayout = (screen: UseScreenSizesReturn): UseXplorTraderLayoutReturn => {
    const lang = useLanguage();
    const mobileLayoutNode = useAppSelector(selectXplorTraderMobileLayoutNode);
    const tabletLayoutNode = useAppSelector(selectXplorTraderTabletLayoutNode);
    const laptopLayoutNode = useAppSelector(selectXplorTraderLaptopLayoutNode);
    const desktopLayoutNode = useAppSelector(selectXplorTraderDesktopLayoutNode);

    const config: ConfigType = useMemo(() => {
        return {
            Tickers: {
                title: lang.modules.tickers
            },
            Blotter: {
                title: lang.modules.blotter
            },
            Chart: {
                title: lang.modules.chart
            },
            'Account Value': {
                title: lang.modules['account-value'],
                component: (
                    <div id="accountValue" className="h-full bg-brand-background">
                        <AccountValue />
                    </div>
                )
            },
            Markets: {
                title: lang.modules.markets,
                component: (
                    <div id="market-list" className="bg-brand-background h-full max-h-full overflow-hidden">
                        <MarketList isTraderMode />
                    </div>
                )
            },
            'Order Book': {
                title: lang.modules['order-book'],
                component: <OrderBook />
            }
        };
    }, [lang]);

    const xplorTraderLayout = useMemo(() => {
        switch (screen.current) {
            case 'mobile':
                return {
                    layout: mobileLayoutNode || DEFAULT_MOBILE_LAYOUT,
                    default_layout: DEFAULT_MOBILE_LAYOUT
                };
            case 'tablet':
                return {
                    layout: mobileLayoutNode || DEFAULT_MOBILE_LAYOUT,
                    default_layout: DEFAULT_MOBILE_LAYOUT
                };
            case 'laptop':
                return {
                    layout: laptopLayoutNode || DEFAULT_LAPTOP_LAYOUT,
                    default_layout: DEFAULT_LAPTOP_LAYOUT
                };
            case 'desktop':
                return {
                    layout: desktopLayoutNode || DEFAULT_LAYOUT,
                    default_layout: DEFAULT_LAYOUT
                };
            default:
                return {
                    layout: desktopLayoutNode || DEFAULT_LAYOUT,
                    default_layout: DEFAULT_LAYOUT
                };
        }
    }, [screen.current, mobileLayoutNode, tabletLayoutNode, laptopLayoutNode, desktopLayoutNode]);

    return { ...xplorTraderLayout, config };
};

export default useXplorTraderLayout;
